import React, { useRef, useEffect, useState } from 'react';

/**
 * MarqueeText – scrollt nur wenn der Text breiter ist als der Container.
 * Misst immer mit einem versteckten Span → kein Flackern beim State-Wechsel.
 * Animation basiert auf konkreten Pixel-Werten (kein %-Bug).
 */
export default function MarqueeText({ children, speed = 40, style = {} }) {
  const outerRef = useRef(null);
  const hiddenRef = useRef(null);
  const [anim, setAnim] = useState(null); // null = kein Scroll, sonst { shift, duration }

  useEffect(() => {
    const compute = () => {
      if (!outerRef.current || !hiddenRef.current) return;
      const cw = outerRef.current.clientWidth;
      const tw = hiddenRef.current.scrollWidth;
      if (tw > cw) {
        const gap = Math.max(cw, 40); // Abstand zwischen den zwei Kopien
        setAnim({ shift: tw + gap, gap, duration: (tw + gap) / speed });
      } else {
        setAnim(null);
      }
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [children, speed]);

  const animName = anim ? `mqee${Math.round(anim.shift)}` : '';

  return (
    <div
      ref={outerRef}
      style={{ overflow: 'hidden', whiteSpace: 'nowrap', position: 'relative', ...style }}
    >
      {/* Versteckter Span für Breitenermittlung – immer im DOM */}
      <span
        ref={hiddenRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          fontSize: 'inherit',
          fontFamily: 'inherit',
          fontWeight: 'inherit',
          letterSpacing: 'inherit',
        }}
      >
        {children}
      </span>

      {anim ? (
        <>
          <style>{`@keyframes ${animName}{0%{transform:translateX(0)}100%{transform:translateX(-${anim.shift}px)}}`}</style>
          <span
            style={{
              display: 'inline-block',
              animation: `${animName} ${anim.duration}s linear infinite`,
            }}
          >
            <span style={{ display: 'inline-block', paddingRight: anim.gap }}>{children}</span>
            <span style={{ display: 'inline-block', paddingRight: anim.gap }}>{children}</span>
          </span>
        </>
      ) : (
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {children}
        </span>
      )}
    </div>
  );
}

